import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Club {
  id: string;
  name: string;
  sport?: string;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  website?: string;
  created_at: string;
  clubs?: Club[];
}

interface OrganizationCardProps {
  organization: Organization;
  hoveredOrgId: string | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onEdit: (org: Organization) => void;
  onDelete: (id: string) => void;
  onManageMembers: (orgId: string) => void;
}

export const OrganizationCard = ({
  organization,
  hoveredOrgId,
  onMouseEnter,
  onMouseLeave,
  onEdit,
  onDelete,
  onManageMembers
}: OrganizationCardProps) => {
  // Get unique sports from clubs
  const uniqueSports = React.useMemo(() => {
    if (!organization.clubs) return [];
    
    const sports = organization.clubs
      .map(club => club.sport)
      .filter((sport): sport is string => !!sport);
    
    return [...new Set(sports)];
  }, [organization.clubs]);

  const displaySports = uniqueSports.slice(0, 3);
  const remainingSportsCount = uniqueSports.length - 3;

  const isHovered = hoveredOrgId === organization.id;

  return (
    <Card 
      className="relative overflow-hidden h-48"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={organization.logo_url} alt={organization.name} />
            <AvatarFallback>
              <Building2 className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          {organization.name}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {organization.description && (
          <p className="text-sm text-muted-foreground mb-2">
            {organization.description}
          </p>
        )}
        
        {/* Sport Pills */}
        {uniqueSports.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {displaySports.map((sport) => (
              <Badge key={sport} variant="secondary" className="text-xs">
                {sport}
              </Badge>
            ))}
            {remainingSportsCount > 0 && (
              <Badge variant="outline" className="text-xs">
                +{remainingSportsCount}
              </Badge>
            )}
          </div>
        )}
        
        {organization.website && (
          <a 
            href={organization.website} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline"
          >
            Visit Website
          </a>
        )}
        <div className="text-xs text-muted-foreground mt-2">
          Created: {new Date(organization.created_at).toLocaleDateString()}
        </div>
      </CardContent>
      
      {/* Manage Org Button - Always rendered but visibility controlled */}
      <div className={`absolute bottom-3 right-3 transition-opacity duration-200 ${
        isHovered ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <Button
          size="sm"
          onClick={() => onManageMembers(organization.id)}
          className="flex items-center gap-2"
        >
          <span className="hidden sm:inline">Manage Org</span>
          <span className="sm:hidden">Manage</span>
        </Button>
      </div>
    </Card>
  );
};
